import { useState } from 'react';
import { ChevronDown, ChevronUp, Eye, EyeOff, Plus, X } from 'lucide-react';
import SpaceLinkCards from '../SpaceLinkCards';
import HubTileArt from '../../components/HubTileArt';
import { LINK_TILE_CATALOGUE, MAX_LINK_SLOTS, tileById, shownSlots } from '../linkTiles';

// THE LINK TILES ELEMENT: a row of square illustrated tiles for the key links (the ones on the Sales and Admin Space home pages). Up to ten show at once; the list can hold more,
// and each link has an eye: lit = showing on the page, crossed = in the list but not showing.
// props = { slots: [{ key, label?, hidden? }] }: which links, in what order, what each is called (blank = its usual name), and whether it is showing.

// How it is drawn on a home page (a slot that is not showing is left out).
export const LinkTilesRender = ({ user, props }) => <SpaceLinkCards user={user} slots={props.slots || []} />;

// How it looks in the Spaces Builder (the element list and the page preview): the same tiles, drawn the same way, without anything that opens.
export const LinkTilesPreview = ({ props, dark }) => (
  <div className="v3-sp2-row" data-testid="link-tiles-preview">
    {shownSlots(props.slots).map((slot) => ({ slot, tile: tileById(slot.key) })).filter((entry) => entry.tile).map(({ slot, tile }) => (
      <div key={tile.id} className="v3-sp2-slot">
        <div className="v3-sp2-card" style={{ '--sc': tile.accent }}>
          <span className="v3-sp2-art"><HubTileArt artKey={tile.art} theme={dark ? 'dark' : 'light'} className="v3-sp2-svg" /></span>
          <span className="v3-sp2-label"><span className="v3-sp2-name">{(slot.label || '').trim() || tile.label}</span></span>
        </div>
      </div>
    ))}
  </div>
);

// Changing what it holds: choose the links, put them in order, rename them, say which are showing (never more than ten), take them out, or add more (each link once).
export const LinkTilesEditor = ({ props, onChange }) => {
  const slots = props.slots || [];
  const used = new Set(slots.map((slot) => slot.key));
  const free = LINK_TILE_CATALOGUE.filter((tile) => !used.has(tile.id));
  const showing = shownSlots(slots).length;
  const full = showing >= MAX_LINK_SLOTS;
  const [pick, setPick] = useState('');
  const set = (next) => onChange({ ...props, slots: next });
  const move = (index, step) => {
    const to = index + step;
    if (to < 0 || to >= slots.length) return;
    const next = [...slots];
    [next[index], next[to]] = [next[to], next[index]];
    set(next);
  };
  const toggleShowing = (index) => {
    const slot = slots[index];
    const next = [...slots];
    if (slot.hidden) { if (full) return; const { hidden, ...rest } = slot; next[index] = rest; } else { next[index] = { ...slot, hidden: true }; }
    set(next);
  };
  const chosen = free.find((tile) => tile.id === pick) || free[0];
  return (
    <div className="space-y-2" data-testid="link-tiles-editor">
      <p className="sb-note" data-testid="link-tiles-count"><strong>{showing} of {MAX_LINK_SLOTS}</strong> showing · {slots.length} in the list</p>
      <p className="sb-note">Press the eye to show or hide a tile on the page. A tile that is hidden stays in the list.</p>
      {slots.map((slot, index) => {
        const tile = tileById(slot.key);
        const off = !!slot.hidden;
        return (
          <div key={slot.key} className={`flex items-stretch gap-2 sb-slot ${off ? 'sb-slot--hidden' : ''}`} data-testid={`lt-slot-${slot.key}`}>
            <span className="w-1.5 rounded-full flex-none sb-slot-accent" style={{ background: tile ? tile.accent : '#9ca3af' }} />
            <div className="min-w-0 flex-1 space-y-1.5">
              <div className="flex items-center gap-1.5">
                <select
                  value={slot.key}
                  onChange={(event) => { const next = [...slots]; next[index] = off ? { key: event.target.value, hidden: true } : { key: event.target.value }; set(next); }}
                  className="sb-field flex-1"
                  data-testid={`lt-slot-${slot.key}-link`}
                  aria-label="Link"
                >
                  {[tile, ...free].filter(Boolean).map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
                </select>
                <button
                  type="button"
                  className={`sb-iconbtn ${off ? '' : 'sb-iconbtn--on'}`}
                  disabled={off && full}
                  onClick={() => toggleShowing(index)}
                  aria-pressed={!off}
                  aria-label={off ? 'Show this tile on the page' : 'Hide this tile from the page'}
                  title={off ? (full ? `${MAX_LINK_SLOTS} tiles are already showing. Hide one first.` : 'Hidden. Press to show it on the page') : 'Showing on the page. Press to hide it'}
                  data-testid={`lt-slot-${slot.key}-eye`}
                >
                  {off ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button type="button" className="sb-iconbtn" disabled={index === 0} onClick={() => move(index, -1)} aria-label="Move earlier" data-testid={`lt-slot-${slot.key}-up`}><ChevronUp className="w-4 h-4" /></button>
                <button type="button" className="sb-iconbtn" disabled={index === slots.length - 1} onClick={() => move(index, 1)} aria-label="Move later" data-testid={`lt-slot-${slot.key}-down`}><ChevronDown className="w-4 h-4" /></button>
                <button type="button" className="sb-iconbtn" onClick={() => set(slots.filter((other) => other.key !== slot.key))} aria-label="Take out" data-testid={`lt-slot-${slot.key}-remove`}><X className="w-4 h-4" /></button>
              </div>
              <input
                value={slot.label || ''}
                maxLength={40}
                placeholder={tile ? `Name under the tile (${tile.label})` : ''}
                onChange={(event) => { const next = [...slots]; next[index] = { ...slot, label: event.target.value }; set(next); }}
                className="sb-field w-full"
                data-testid={`lt-slot-${slot.key}-label`}
                aria-label="Name shown under the tile"
              />
            </div>
          </div>
        );
      })}
      {free.length > 0 ? (
        <div className="flex items-center gap-1.5 pt-1">
          <select value={chosen ? chosen.id : ''} onChange={(event) => setPick(event.target.value)} className="sb-field flex-1" data-testid="lt-add-select" aria-label="Link to add">
            {free.map((tile) => <option key={tile.id} value={tile.id}>{tile.label}</option>)}
          </select>
          <button
            type="button"
            className="h-8 px-3 flex-none rounded-lg text-xs font-semibold text-white flex items-center gap-1 hover:opacity-90"
            style={{ backgroundColor: '#ee7203' }}
            onClick={() => { if (chosen) { set([...slots, full ? { key: chosen.id, hidden: true } : { key: chosen.id }]); setPick(''); } }}
            data-testid="lt-add"
          ><Plus className="w-3.5 h-3.5" /> Add link</button>
        </div>
      ) : (
        <p className="sb-note" data-testid="lt-full">Every link that can be a tile is in the list.</p>
      )}
      {full && free.length > 0 && <p className="sb-note" data-testid="lt-full-note">{MAX_LINK_SLOTS} are showing, so a link you add starts hidden. Hide one with its eye to show another.</p>}
    </div>
  );
};
